import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import chinaGeo from 'echarts-china-map/lib/china.json';
import { isOverseas } from '../../utils/i18n';

// 省份英文名映射
const EN_NAMES = {
    '北京市': 'Beijing', '上海市': 'Shanghai',
    '山西省': 'Shanxi', '河北省': 'Hebei', '安徽省': 'Anhui',
    '辽宁省': 'Liaoning', '陕西省': 'Shaanxi', '广东省': 'Guangdong',
    '河南省': 'Henan', '福建省': 'Fujian', '浙江省': 'Zhejiang',
    '湖北省': 'Hubei', '湖南省': 'Hunan', '甘肃省': 'Gansu',
    '山东省': 'Shandong', '江苏省': 'Jiangsu', '江西省': 'Jiangxi',
    '宁夏回族自治区': 'Ningxia', '新疆维吾尔自治区': 'Xinjiang',
};

// 已落地运营省份（蓝色高亮填充）
const ACTIVE_PROVINCES = [
    '山西省', '河北省', '安徽省', '辽宁省', '陕西省', '广东省', '河南省', '福建省', '北京市', '浙江省', '湖北省'
];

// 近期规划省份（浅灰填充虚线）
const PLANNED_PROVINCES = [
    '湖南省', '甘肃省', '宁夏回族自治区', '山东省', '江苏省', '上海市', '江西省', '新疆维吾尔自治区'
];

// 动态扩散点位
const SCATTER_POINTS = [
    { name: '辽宁', value: [123.43, 41.80] },
    { name: '河北', value: [114.48, 38.03] },
    { name: '陕西', value: [108.95, 34.27] },
    { name: '四川', value: [104.07, 30.67] },
    { name: '湖北', value: [114.31, 30.52] },
    { name: '浙江', value: [120.15, 30.28] },
    { name: '广东', value: [113.26, 23.13] },
];

// 手动修正省份的 label 中心点（确保文字居中）
const CENTER_POINTS = {
    '甘肃省': [103.8, 36.0],
    '内蒙古自治区': [112.0, 42.0],
    '广东省': [113.5, 23.5],
    '河北省': [115.0, 39.0],
    '江苏省': [119.8, 33.0],
    '陕西省': [108.9, 35.0],
    '广西壮族自治区': [108.3, 23.7],
    '山西省': [112.5, 37.5],
    '辽宁省': [123.0, 41.5],
    '安徽省': [117.2, 31.8],
    '福建省': [118.3, 26.0],
    '浙江省': [120.2, 29.2],
    '湖北省': [112.3, 30.8],
    '河南省': [113.6, 33.8],
    '北京市': [116.4, 40.0],
    '湖南省': [111.7, 27.6],
    '山东省': [117.9, 36.3],
    '江西省': [115.8, 27.6],
    '上海市': [121.5, 31.2],
    '宁夏回族自治区': [106.2, 37.3],
    '新疆维吾尔自治区': [85.0, 41.5],
};

// 为 GeoJSON 数据注入 cp 中心点
const geoData = JSON.parse(JSON.stringify(chinaGeo));
geoData.features.forEach(f => {
    const name = f.properties.name;
    if (CENTER_POINTS[name]) {
        f.properties.cp = CENTER_POINTS[name];
    }
});

echarts.registerMap('china', geoData);

export default function ChinaMap() {
    const chartRef = useRef(null);
    const instanceRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return;
        const chart = echarts.init(chartRef.current);
        instanceRef.current = chart;

        // 构建各省数据
        const allProvinces = chinaGeo.features.map(f => f.properties.name);
        const mapData = allProvinces.map(name => {
            if (ACTIVE_PROVINCES.includes(name)) {
                return {
                    name,
                    value: 1,
                    itemStyle: {
                        areaColor: 'rgba(59, 130, 246, 0.6)',
                        borderColor: 'rgba(96, 165, 250, 0.8)',
                        borderWidth: 1,
                    },
                    label: { show: true, color: '#97B5EF', fontSize: 11 },
                };
            }
            if (PLANNED_PROVINCES.includes(name)) {
                return {
                    name,
                    value: 2,
                    itemStyle: {
                        areaColor: 'transparent',
                        borderColor: 'rgba(148, 163, 184, 0.5)',
                        borderWidth: 1,
                        borderType: 'dashed',
                    },
                    label: { show: true, color: '#FFFFFF88', fontSize: 11 },
                };
            }
            return {
                name,
                value: 0,
                label: { show: false },
            };
        });

        const option = {
            backgroundColor: '#06102C',
            geo: {
                map: 'china',
                roam: false,
                zoom: 1.2,
                center: [104, 35],
                silent: true,
                itemStyle: {
                    areaColor: 'transparent',
                    borderColor: 'transparent',
                },
                label: { show: false },
            },
            series: [
                {
                    type: 'map',
                    map: 'china',
                    roam: false,
                    zoom: 1.2,
                    center: [104, 35],
                    label: {
                        show: true,
                        color: '#FFFFFF88',
                        fontSize: 10,
                        formatter: function (params) {
                            if (ACTIVE_PROVINCES.includes(params.name) || PLANNED_PROVINCES.includes(params.name)) {
                                return isOverseas ? (EN_NAMES[params.name] || params.name) : params.name;
                            }
                            return '';
                        },
                    },
                    itemStyle: {
                        areaColor: '#FFFFFF1D',
                        borderColor: 'rgba(96, 165, 250, 0.3)',
                        borderWidth: 1,
                    },
                    emphasis: {
                        disabled: true,
                    },
                    select: {
                        disabled: true,
                    },
                    data: mapData,
                },
                {
                    type: 'effectScatter',
                    coordinateSystem: 'geo',
                    geoIndex: 0,
                    data: SCATTER_POINTS,
                    symbolSize: 16,
                    rippleEffect: {
                        number: 2,
                        brushType: 'stroke',
                        scale: 4,
                        period: 3,
                    },
                    showEffectOn: 'render',
                    itemStyle: {
                        color: '#3B82F6',
                        shadowBlur: 8,
                        shadowColor: 'rgba(59, 130, 246, 0.6)',
                    },
                },
            ],
        };

        chart.setOption(option);

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, []);

    return (
        <div
            ref={chartRef}
            style={{ width: '100%', maxWidth: '1050px', height: '785px', margin: '0 auto' }}
        />
    );
}
